import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import {
  IdGenerator,
  IdGeneratorEntityType,
} from '@src/editor/services/IdGenerator';
import { LogManager } from '@src/utility/Logger';

import { AbstractCommand } from '../AbstractCommand';

import type { KnownPunctuation } from '../../interface/IVideoEditorCommands';
import type { VideoEditor } from '../../VideoEditor';
import type { ClipTranscriptToken } from '@src/editor/types';

const logger = LogManager.getLogger('transcript:punctuation');

/**
 * Adds a punctuation token (comma / period / newline) into a transcript segment immediately after a given token index.
 * Space remains unsupported (treated as layout / word separation rather than an inserted punctuation token).
 * Undo removes only the inserted punctuation and restores previous segment object.
 */
export class TranscriptAddPunctuationCommand extends AbstractCommand {
  private readonly code: KnownPunctuation;
  private readonly segmentId: string;
  private readonly afterIndex: number; // index within wordsWithPunctuation after which punctuation is inserted

  private previousSegment: ClipTranscriptSegment | null = null;
  private element: VideoTranscriptElement | null = null;

  constructor(
    editor: VideoEditor,
    code: KnownPunctuation,
    segmentId: string,
    afterIndex: number,
  ) {
    super(editor);
    this.code = code;
    this.segmentId = segmentId;
    this.afterIndex = afterIndex;
  }

  execute(): boolean {
    if (!this.mapCodeToChar(this.code)) {
      logger.error('Unsupported punctuation code', { code: this.code });
      return false;
    }

    const track = this.editor.getVideoTranscriptTrack();
    if (!track) {
      logger.error('No transcript track available');
      return false;
    }

    const el = track.transcriptSegmentElements.find(
      e => e.getClipTranscriptSegment().id === this.segmentId,
    );
    if (!(el instanceof VideoTranscriptElement)) {
      logger.error('Segment element not found or invalid', {
        segmentId: this.segmentId,
      });
      return false;
    }
    this.element = el;

    const segment = el.getClipTranscriptSegment();
    const tokens = segment.getWordsWithPunctuation();

    const newSegment = this.createNewSegmentWithPunctuationAdded(
      tokens,
      segment,
    );
    if (!newSegment) return false;

    this.previousSegment = segment;
    logger.debug(
      `Replacing ${segment.id} with ${newSegment.id} to add punctuation.`,
    );
    el.setClipTranscriptSegment(newSegment);

    // Recalculate timeline and reconcile dependent tracks
    this.recalculateTimeline();

    this.editor.markDirty('transcript_add_punctuation');
    return true;
  }

  undo(): boolean {
    if (!this.element || !this.previousSegment) return false;
    this.element.setClipTranscriptSegment(this.previousSegment);
    this.editor.markDirty('transcript_add_punctuation_undo');
    return true;
  }

  getName(): string {
    return 'TranscriptAddPunctuationCommand';
  }

  getDescription(): string {
    return `Adds punctuation ${this.code} into segment ${this.segmentId} after index: ${this.afterIndex}`;
  }

  private createNewSegmentWithPunctuationAdded(
    tokens: ClipTranscriptToken[],
    segment: ClipTranscriptSegment,
  ): ClipTranscriptSegment | false {
    // Bounds: allow insertion after last index (append). Disallow negative
    // other than -1 (prepend at start). If afterIndex == tokens.length - 1
    // append; if == -1 prepend.
    if (this.afterIndex < -1 || this.afterIndex > tokens.length - 1) {
      logger.error('afterIndex out of bounds', {
        afterIndex: this.afterIndex,
        validRange: `-1 to ${tokens.length - 1}`,
      });
      return false;
    }

    const punctuationChar = this.mapCodeToChar(this.code);
    if (!punctuationChar) {
      logger.error('Unsupported punctuation code', { code: this.code });
      return false;
    }

    const insertPos = this.afterIndex + 1; // position new token should occupy

    // Build new punctuation token
    const generator = IdGenerator.get(
      IdGeneratorEntityType.TranscriptToken,
      'hex',
    );
    const punctToken: ClipTranscriptToken = {
      id: generator.getNextIdHex(),
      type: 'punct',
      value: punctuationChar,
      state: 'added',
      initial_index: -1,
    };

    // Create new tokens array immutably
    const newTokens: ClipTranscriptToken[] = [
      ...tokens.slice(0, insertPos),
      punctToken,
      ...tokens.slice(insertPos),
    ];

    // Preserve media time boundaries
    let newSegment = new ClipTranscriptSegment(
      newTokens,
      segment.mediaStart,
      segment.mediaEnd,
    );

    // Capitalization rule: if inserting a Period and there is a following text
    // token, capitalize it.
    if (this.code === 'Period') {
      const following = newSegment
        .getWordsWithPunctuation()
        .slice(insertPos + 1)
        .find(t => t.type === 'text' && t.state !== 'removed');
      if (following && following.type === 'text' && following.value) {
        const originalValue = following.value;
        const capitalized =
          originalValue.charAt(0).toUpperCase() + originalValue.slice(1);
        if (capitalized !== originalValue) {
          // Create a modified token list with capitalized value (immutably)
          const updatedTokens = newSegment
            .getWordsWithPunctuation()
            .map(t =>
              t.id === following.id ? { ...t, value: capitalized } : t,
            );
          newSegment = new ClipTranscriptSegment(
            updatedTokens,
            newSegment.mediaStart,
            newSegment.mediaEnd,
          );
        }
      }
    }

    return newSegment;
  }

  private mapCodeToChar(code: KnownPunctuation): string | null {
    switch (code) {
      case 'Comma':
        return ',';
      case 'Period':
        return '.';
      case 'Space':
        return null; // unsupported per current semantics
      case 'Enter':
        return '\n';
      default:
        return null;
    }
  }
}
